import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Trophy, Zap } from 'lucide-react'
import {
  getGetApiCompaniesCurrentServicemodulesLeaderboardsServiceModuleIdQueryKey,
  getGetApiCompaniesCurrentServicemodulesQueryKey,
  useGetApiCompaniesCurrentServicemodulesLeaderboardsActionModules,
  usePatchApiCompaniesCurrentServicemodulesLeaderboardsServiceModuleId,
} from '@/api/generated/company-management-user/company-management-user'
import type {
  ActionModuleResponse,
  LeaderboardServiceModuleModel,
  UpdateLeaderboardServiceModuleModel,
} from '@/api/model'
import { LeaderboardRankingOrder } from '@/api/model/leaderboardRankingOrder'
import { Label } from '@/components/shadcn/label'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/shadcn/select'
import { MultiSelectPopover } from '@/components/common/MultiSelectPopover'
import { notify } from '@/lib/notify'
import { useStandardServiceModuleFields } from '../useStandardServiceModuleFields'
import { StandardModuleFields } from '../EditServiceModuleFields'
import { EditServiceModuleDialog } from '../EditServiceModuleDialog'
import { AutoTranslateAllButton } from '@/components/common/translations/AutoTranslateAllButton'

interface Props {
  module: LeaderboardServiceModuleModel
  onClose: () => void
}

/**
 * Editor for Leaderboard modules: standard fields (column 1) + the leaderboard
 * specifics (column 2 — ranking direction + included action modules).
 * Saves everything in a single request via the Leaderboard endpoint.
 */
export function EditLeaderboardServiceModule({ module, onClose }: Props) {
  const { t } = useTranslation(['service-modules-leaderboard', 'service-modules'])
  const std = useStandardServiceModuleFields(module)
  const [autoTranslateAll, setAutoTranslateAll] = useState(false)
  const [rankingOrder, setRankingOrder] = useState<LeaderboardRankingOrder>(
    module.rankingOrder ?? LeaderboardRankingOrder.BiggerIsBetter,
  )
  const [actionModuleIds, setActionModuleIds] = useState<number[]>(module.assignedActionModuleIds ?? [])

  const queryClient = useQueryClient()
  const patch = usePatchApiCompaniesCurrentServicemodulesLeaderboardsServiceModuleId()

  // Only action modules that can feed a ranking are selectable: via this endpoint the
  // backend returns exactly the score-based modules (RewardCalculationType with
  // ExpectedIncentivationDataType == Score), filtered to the visibility/groups of the
  // leaderboard module itself. If visibility/groups change, React Query re-fetches
  // automatically (the params flow into the query key).
  const { data: actionModulesData } = useGetApiCompaniesCurrentServicemodulesLeaderboardsActionModules({
    isCompanyWideAssigned: std.isCompanyWide,
    groupIds: std.selectedGroupIds,
  })
  const actionModules = (((actionModulesData as any)?.data ?? actionModulesData ?? []) as ActionModuleResponse[])
    .filter((a) => a.id != null)
    .map((a) => ({ id: String(a.id), label: a.name ?? t('editLeaderboard.actionModuleFallback', { id: a.id }) }))

  async function handleSave() {
    const payload: UpdateLeaderboardServiceModuleModel = {
      ...std.buildBasePayload(),
      assignedActionModuleIds: actionModuleIds,
      rankingOrder,
    }
    try {
      await patch.mutateAsync({ serviceModuleId: module.id, data: payload })
      await std.saveTranslations()
      std.triggerTranslation(String(module.id), autoTranslateAll)
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getGetApiCompaniesCurrentServicemodulesLeaderboardsServiceModuleIdQueryKey(module.id),
        }),
        queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentServicemodulesQueryKey() }),
      ])
      notify.success(t('toasts.saved', { ns: 'service-modules' }))
      onClose()
    } catch (error) {
      notify.apiError(error, t('toasts.saveError', { ns: 'service-modules' }))
    }
  }

  const specific = (
    <>
      <div className="flex flex-col gap-1.5">
        <Label className="flex items-center gap-1.5">
          <Trophy className="size-3.5" />
          {t('editLeaderboard.rankingOrderLabel')}
        </Label>
        <Select value={rankingOrder} onValueChange={(v) => setRankingOrder(v as LeaderboardRankingOrder)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(LeaderboardRankingOrder).map((order) => (
              <SelectItem key={order} value={order}>{t(`editLeaderboard.rankingOrder.${order}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{t('editLeaderboard.rankingOrderHint')}</p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="flex items-center gap-1.5">
          <Zap className="size-3.5" />
          {t('editLeaderboard.actionModulesLabel')}
        </Label>
        <MultiSelectPopover
          icon={Zap}
          label={t('editLeaderboard.actionModulesButton')}
          emptyText={t('editLeaderboard.actionModulesEmpty')}
          options={actionModules}
          selectedIds={actionModuleIds.map(String)}
          onChange={(ids) => setActionModuleIds(ids.map(Number))}
        />
        <p className="text-xs text-muted-foreground">{t('editLeaderboard.actionModulesHint')}</p>
      </div>
    </>
  )

  return (
    <EditServiceModuleDialog
      id={module.id}
      moduleName={module.name}
      dialogTitle={t('editLeaderboard.title')}
      onClose={onClose}
      standard={<StandardModuleFields fields={std} moduleType={module.type} autoTranslateAll={autoTranslateAll} />}
      specific={specific}
      onSave={handleSave}
      isSaving={patch.isPending}
      autoTranslateButton={
        <AutoTranslateAllButton
          properties={std.autoTranslateProperties}
          autoTranslateAll={autoTranslateAll}
          onAutoTranslateAllChange={setAutoTranslateAll}
        />
      }
    />
  )
}
